import { useMemo, type CSSProperties } from 'react';
import { AlertTriangle } from 'lucide-react';
import { tokens } from '../theme/tokens';
import { getAuthErrorCopy, getLinkbookSignInUrl } from '../utils/authErrorCopy';
import { buildNativeAppLink } from '../utils/nativeAppLink';

export default function AuthError() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const copy = getAuthErrorCopy(params.get('error'));
  const description = params.get('error_description');
  const app = params.get('app');
  const qrUrl = buildNativeAppLink('cittam://', null);
  const linkbookUrl = getLinkbookSignInUrl(import.meta.env.VITE_LINKBOOK_SIGN_IN_URL as string);
  const qrAction = { href: qrUrl, label: copy.qrLabel };
  const linkbookAction = { href: linkbookUrl, label: copy.linkbookLabel };
  const [primaryAction, secondaryAction] =
    app === 'qr' ? [qrAction, linkbookAction] : [linkbookAction, qrAction];

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <AlertTriangle size={56} color={tokens.semantic.warning} style={styles.icon} />
        <h1 style={styles.title}>{copy.title}</h1>
        <p style={styles.subtitle}>{copy.body}</p>
        {description && <p style={styles.detail}>{description}</p>}
        <div style={styles.actions}>
          <a href={primaryAction.href} style={styles.button}>
            {primaryAction.label}
          </a>
          <a href={secondaryAction.href} style={styles.secondaryButton}>
            {secondaryAction.label}
          </a>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
  },
  card: {
    background: tokens.bg.card,
    backdropFilter: 'blur(12px)',
    borderRadius: tokens.radius.xl,
    padding: 32,
    border: `1px solid ${tokens.border.default}`,
    textAlign: 'center',
  },
  icon: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
    color: tokens.text.primary,
  },
  subtitle: {
    fontSize: 14,
    color: tokens.text.muted,
    marginBottom: 18,
    lineHeight: 1.6,
  },
  detail: {
    fontSize: 13,
    color: tokens.text.secondary,
    background: 'rgba(15, 23, 42, 0.55)',
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.md,
    padding: 12,
    marginBottom: 20,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 24px',
    fontSize: 16,
    fontWeight: 600,
    color: '#ffffff',
    background: `linear-gradient(135deg, ${tokens.brand.primary} 0%, ${tokens.brand.accent} 100%)`,
    border: 'none',
    borderRadius: tokens.radius.md,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 24px',
    fontSize: 16,
    fontWeight: 600,
    color: tokens.text.primary,
    background: 'rgba(15, 23, 42, 0.55)',
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.md,
    cursor: 'pointer',
    textDecoration: 'none',
  },
};
